import axios from 'axios';

const BACKEND_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API_BASE = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

const api = axios.create({
    baseURL: API_BASE,
    headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
    },
});

// Request interceptor - add auth token and cache busting
api.interceptors.request.use((config) => {
    // Remove trailing slash if present
    if (config.url && config.url.endsWith('/') && config.url.length > 1) {
        config.url = config.url.slice(0, -1);
    }
    
    // Add cache-busting timestamp to ALL GET requests
    if (config.method === 'get') {
        config.params = {
            ...config.params,
            _t: Date.now()
        };
    }
    
    // Add auth token
    const token = localStorage.getItem('access_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
});

// Response interceptor - handle auth errors
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        
        if (error.response?.status === 401 && !originalRequest._retry) {
            const refreshToken = localStorage.getItem('refresh_token');
            
            if (refreshToken) {
                originalRequest._retry = true;
                try {
                    const response = await axios.post(`${API_BASE}/auth/refresh`, {
                        refresh_token: refreshToken
                    });
                    
                    const { access_token } = response.data?.data?.access_token || response.data?.access_token;
                    localStorage.setItem('access_token', access_token);
                    
                    originalRequest.headers.Authorization = `Bearer ${access_token}`;
                    return api(originalRequest);
                } catch (refreshError) {
                    localStorage.removeItem('access_token');
                    localStorage.removeItem('refresh_token');
                    localStorage.removeItem('user');
                    window.location.href = '/login';
                    return Promise.reject(refreshError);
                }
            } else {
                localStorage.removeItem('access_token');
                localStorage.removeItem('refresh_token');
                localStorage.removeItem('user');
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

// ==================== HELPER FUNCTIONS ====================

/**
 * Extract data from API response (handles nested responses)
 */
export const extractData = (response, key = null) => {
    const data = response?.data?.data || response?.data || response;
    if (key) {
        return data?.[key] || data;
    }
    return data;
};

/**
 * Force refresh by clearing browser cache for API calls
 */
export const forceRefresh = () => {
    // Clear any cached data
    if ('caches' in window) {
        caches.keys().then(names => {
            names.forEach(name => {
                caches.delete(name);
            });
        });
    }
};

// ==================== AUTH API ====================
export const authAPI = {
    register: (data) => api.post('/auth/register', data),
    login: (data) => api.post('/auth/login', data),
    logout: () => api.post('/auth/logout'),
    refreshToken: (refreshToken) => api.post('/auth/refresh', { refresh_token: refreshToken }),
    getMe: () => api.get('/auth/me'),
    forgotPassword: (data) => api.post('/auth/forgot-password', data),
    resetPassword: (data) => api.post('/auth/reset-password', data),
    verifyResetToken: (token) => api.get(`/auth/verify-reset-token?token=${token}`),
    changePassword: (data) => api.post('/auth/change-password', data),
};

// ==================== SALON API ====================
export const salonAPI = {
    // Settings
    getSettings: () => api.get('/salon/settings'),
    updateSettings: (data) => api.put('/salon/settings/update', data),
    updateProfile: (data) => api.put('/salon/settings/update', data),
    
    // Paystack
    updatePaystack: (data) => api.post('/salon/paystack', data),
    updatePaystackSettings: (data) => api.post('/salon/paystack', data),
    
    // Booking Fee
    updateBookingFee: (data) => api.post('/salon/booking-fee', data),
    updateBookingSettings: (data) => api.post('/salon/booking-fee', data),
    
    // Logs
    getLogs: (params) => api.get(`/salon/logs${params ? '?' + params : ''}`),
    exportLogs: (format) => api.get('/salon/logs/export', { 
        params: { format },
        responseType: 'blob' 
    }),
    
    // Dashboard
    getDashboard: () => api.get('/dashboard/stats'),
};

// ==================== REMINDERS & NOTIFICATIONS API ====================
export const remindersAPI = {
    // Get notification settings
    getSettings: () => api.get('/reminders/settings'),
    
    // Update notification settings
    updateSettings: (data) => api.post('/reminders/settings/update', data),
    
    // Send test notification
    sendTestNotification: (data) => api.post('/reminders/test', data),
    
    // Send message to clients
    sendMessage: (data) => api.post('/reminders/send-message', data),
    
    // Legacy alias
    updateReminders: (data) => api.post('/reminders/settings/update', data),
};

// ==================== STAFF API ====================
export const staffAPI = {
    getAll: () => api.get('/staff'),
    create: (data) => api.post('/staff/create', data),
    getOne: (id) => api.get(`/staff/${id}`),
    update: (id, data) => api.put(`/staff/${id}/update`, data),
    delete: (id) => api.delete(`/staff/${id}/delete`),
};

// ==================== SERVICES API ====================
export const servicesAPI = {
    getAll: () => api.get('/services'),
    create: (data) => api.post('/services/create', data),
    getOne: (id) => api.get(`/services/${id}`),
    update: (id, data) => api.put(`/services/${id}/update`, data),
    delete: (id) => api.delete(`/services/${id}/delete`),
};

// ==================== CLIENTS API ====================
export const clientsAPI = {
    getAll: (params) => api.get('/clients', { params }),
    create: (data) => api.post('/clients/create', data),
    getOne: (id) => api.get(`/clients/${id}`),
    update: (id, data) => api.put(`/clients/${id}/update`, data),
    delete: (id) => api.delete(`/clients/${id}/delete`),
};

// ==================== APPOINTMENTS API ====================
export const appointmentsAPI = {
    // List appointments with filters
    getAll: (params) => api.get('/appointments', { params }),
    
    // Get today's appointments
    getToday: () => api.get('/appointments/today'),
    
    // Get upcoming appointments (next 7 days)
    getUpcoming: () => api.get('/appointments/upcoming'),
    
    // Get appointment statistics
    getStats: (params) => api.get('/appointments/stats', { params }),
    
    // Get week appointments
    getWeek: (params) => api.get('/appointments', { params }),
    
    // Create new appointment
    create: (data) => api.post('/appointments/create', data),
    
    // Get single appointment
    getOne: (id) => api.get(`/appointments/${id}`),
    
    // Update appointment
    update: (id, data) => api.put(`/appointments/${id}/update`, data),
    
    // Cancel appointment - returns fresh data
    cancel: async (id) => {
        const response = await api.post(`/appointments/${id}/cancel`);
        return response;
    },
    
    // Confirm appointment - returns fresh data
    confirm: async (id) => {
        const response = await api.post(`/appointments/${id}/confirm`);
        return response;
    },
    
    // Complete appointment - returns fresh data
    complete: async (id) => {
        const response = await api.post(`/appointments/${id}/complete`);
        return response;
    },
    
    // Mark as no-show
    noShow: (id) => api.post(`/appointments/${id}/no-show`),
    
    // Send reminder manually
    sendReminder: (id) => api.post(`/appointments/${id}/remind`),
    
    // Delete appointment
    delete: (id) => api.delete(`/appointments/${id}/delete`),
    
    // Dashboard stats
    getDashboardStats: () => api.get('/dashboard/stats'),
};

// ==================== ACCOUNTING API ====================
export const accountingAPI = {
    // List transactions with optional filters
    getTransactions: (params = {}) => api.get('/transactions', { params }),
    
    // Get accounting summary
    getSummary: (params = {}) => api.get('/accounting/summary', { params }),
    
    // Create income transaction
    createIncome: (data) => api.post('/transactions/create', {
        type: 'income',
        amount: data.amount,
        description: data.description,
        category: data.category || '',
        date: data.date
    }),
    
    // Create expense transaction
    createExpense: (data) => api.post('/transactions/create', {
        type: 'expense',
        amount: data.amount,
        description: data.description,
        category: data.category || '',
        date: data.date
    }),
    
    // Create any transaction type
    createTransaction: (data) => api.post('/transactions/create', data),
    
    // Get single transaction
    getTransaction: (id) => api.get(`/transactions/${id}`),
    
    // Update transaction
    updateTransaction: (id, data) => api.put(`/transactions/${id}/update`, data),
    
    // Delete transaction
    deleteTransaction: (id) => api.delete(`/transactions/${id}/delete`),
};

// ==================== INVOICES API ====================
export const invoicesAPI = {
    getAll: () => api.get('/invoices'),
    generate: (data) => api.post('/invoices/generate', data, { responseType: 'blob' }),
    getDetail: (id) => api.get(`/invoices/${id}`),
    download: (id) => api.get(`/invoices/${id}/download`, { responseType: 'blob' }),
};

// ==================== SUBSCRIPTIONS API ====================
export const subscriptionsAPI = {
    // Get available plans
    getPlans: () => api.get('/subscriptions/plans'),
    
    // Get current subscription status
    getCurrent: () => api.get('/subscriptions/current'),
    
    // Initialize subscription payment
    initialize: (data) => api.post('/subscriptions/initialize', data),
    
    // Verify subscription payment
    verify: (reference) => api.get(`/subscriptions/verify/${reference}`),
    
    // Buy additional email credits
    buyCredits: (data) => api.post('/subscriptions/buy-credits', data),
    
    // Cancel subscription
    cancel: () => api.post('/subscriptions/cancel'),
};

// ==================== PUBLIC BOOKING API ====================
export const publicBookingAPI = {
    // Get salon public info
    getSalon: (salonId) => axios.get(`${API_BASE}/public/salon/${salonId}`),
    
    // Get salon services
    getServices: (salonId) => axios.get(`${API_BASE}/public/salon/${salonId}/services`),
    
    // Get availability for a date
    getAvailability: (salonId, date) => axios.get(`${API_BASE}/public/salon/${salonId}/availability`, {
        params: { date, _t: Date.now() }
    }),
    
    // Create booking
    createBooking: (data) => axios.post(`${API_BASE}/public/booking/create`, data),
    
    // Verify booking payment
    verifyPayment: (data) => axios.post(`${API_BASE}/public/booking/verify`, data),
    
    // Get public businesses list
    getBusinesses: () => axios.get(`${API_BASE}/public/businesses`),
};

// ==================== HEALTH CHECK API ====================
export const healthAPI = {
    check: () => axios.get(`${BACKEND_URL}/health/`),
    ready: () => axios.get(`${BACKEND_URL}/health/ready/`),
    live: () => axios.get(`${BACKEND_URL}/health/live/`),
};

export default api;